import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { KeyboardArrowDown } from '@mui/icons-material';
import { AuditLogRecord, AuditLogConfig } from './AuditLogPanel.types';
import {
	resolveActorName,
	isNewFormat,
	isCreated,
	isDeleted,
	getActionConfig,
} from './AuditLogPanel.utils';
import AuditLogDiffView from './AuditLogDiffView';
import AuditLogSnapshotView from './AuditLogSnapshotView';
import AuditLogLegacyContent from './AuditLogLegacyContent';

interface Props {
	record: AuditLogRecord;
	config: AuditLogConfig;
	users: Record<string, { name: string }>;
	systemActorLabel: string;
	seeUnchangedData: boolean;
	expanded: boolean;
	onToggle: () => void;
}

const DOT_COLORS = {
	created: 'oklch(51% 0.13 255)',
	deleted: 'oklch(48% 0.11 22)',
	updated: 'oklch(60% 0.007 55)',
	// avatar color names from config
	added: 'oklch(51% 0.13 255)',
	edited: 'oklch(60% 0.007 55)',
	syncing: 'oklch(55% 0.06 255)',
	synced: 'oklch(55% 0.06 255)',
} as const;

function getDotColor(record: AuditLogRecord, config: AuditLogConfig): string {
	if (isNewFormat(record)) {
		return DOT_COLORS[record.type] ?? DOT_COLORS.updated;
	}
	// Legacy: try to derive from avatar config
	const cfg = getActionConfig(record.action, config.actions);
	const colorName = cfg?.avatar(record)?.largeIcon?.color;
	return DOT_COLORS[colorName as keyof typeof DOT_COLORS] ?? DOT_COLORS.edited;
}

const dateTimeFormatter = new Intl.DateTimeFormat('da-DK', {
	year: 'numeric',
	month: 'numeric',
	day: 'numeric',
	hour: 'numeric',
	minute: 'numeric',
	second: 'numeric',
});

const useStyles = makeStyles(() => ({
	accordion: {
		background: 'transparent',
		boxShadow: 'none',
		'&::before': { display: 'none' },
		'&.Mui-expanded': { margin: 0 },
	},
	summary: {
		padding: '0 12px 0 0',
		minHeight: '44px !important',
		'&.Mui-expanded': { minHeight: '44px !important' },
		'& .MuiAccordionSummary-content': {
			margin: '8px 0',
			alignItems: 'center',
			gap: 0,
		},
		'&:hover': {
			backgroundColor: 'oklch(97% 0.005 85)',
		},
		'&:focus-visible': {
			outline: '2px solid oklch(51% 0.13 255)',
			outlineOffset: -2,
		},
	},
	dotArea: {
		width: 40,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		zIndex: 1,
		// White halo so the dot reads against the timeline line
		'&::before': {
			content: '""',
			position: 'absolute',
			width: 18,
			height: 18,
			borderRadius: '50%',
			background: 'oklch(99.8% 0.003 85)',
		},
	},
	dot: {
		width: 10,
		height: 10,
		borderRadius: '50%',
		flexShrink: 0,
		position: 'relative',
		zIndex: 1,
	},
	meta: {
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
	},
	actor: {
		fontSize: '1.4rem',
		fontWeight: 600,
		color: 'oklch(32% 0.012 55)',
		marginRight: 4,
	},
	actionLabel: {
		fontSize: '1.4rem',
		color: 'oklch(52% 0.010 55)',
	},
	timestamp: {
		fontSize: '1.1rem',
		color: 'oklch(63% 0.008 55)',
		flexShrink: 0,
		marginLeft: 12,
		letterSpacing: '0.01em',
	},
	expandIcon: {
		color: 'oklch(63% 0.008 55)',
		fontSize: '1.8rem',
		marginLeft: 4,
		flexShrink: 0,
		transition: 'transform 150ms cubic-bezier(0,0,0.2,1)',
	},
	expandIconOpen: {
		transform: 'rotate(180deg)',
	},
	details: {
		paddingTop: 0,
		paddingBottom: 12,
		paddingLeft: 40,
		paddingRight: 12,
	},
}));

function getActionLabel(record: AuditLogRecord, config: AuditLogConfig): string {
	const cfg = getActionConfig(record.action, config.actions);
	if (cfg?.header) {
		return cfg.header(record).title;
	}
	if (isCreated(record)) return 'oprettede';
	if (isDeleted(record)) return 'slettede';
	return 'ændrede';
}

function AuditLogEntry({ record, config, users, systemActorLabel, seeUnchangedData, expanded, onToggle }: Props) {
	const classes = useStyles();
	const { hiddenFields } = config;
	const dotColor = getDotColor(record, config);
	const actorName = resolveActorName(record.changedBy, users, systemActorLabel);
	const actionLabel = getActionLabel(record, config);
	const timestamp = dateTimeFormatter.format(new Date(record.changedAt));

	return (
		<Accordion
			className={classes.accordion}
			expanded={expanded}
			onChange={onToggle}
			elevation={0}
			TransitionProps={{ unmountOnExit: true }}
		>
			<AccordionSummary className={classes.summary} aria-expanded={expanded}>
				<div className={classes.dotArea}>
					<div className={classes.dot} style={{ background: dotColor }} />
				</div>
				<div className={classes.meta}>
					<span className={classes.actor}>{actorName}</span>
					<span className={classes.actionLabel}>{actionLabel}</span>
				</div>
				<span className={classes.timestamp}>{timestamp}</span>
				<KeyboardArrowDown
					className={`${classes.expandIcon} ${expanded ? classes.expandIconOpen : ''}`}
					fontSize="small"
				/>
			</AccordionSummary>
			<AccordionDetails className={classes.details}>
				{isNewFormat(record) ? (
					record.type === 'created' || record.type === 'deleted' ? (
						<AuditLogSnapshotView record={record} hiddenFields={hiddenFields} />
					) : record.type === 'updated' ? (
						<AuditLogDiffView record={record} hiddenFields={hiddenFields} />
					) : null
				) : (
					<AuditLogLegacyContent record={record} config={config} seeUnchangedData={seeUnchangedData} />
				)}
			</AccordionDetails>
		</Accordion>
	);
}

export default AuditLogEntry;
